import React, { createContext, useContext, useState, useRef, ReactNode } from 'react';

export interface PatientSummaryItem {
  field: string;
  value: string;
}

export interface TemplateItem {
  specialist?: string;
  template?: string;
  [key: string]: any;
}

export interface ClinicalCaseData {
  patient_age?: number;
  patient_gender?: string;
  icd10_code?: string;
  rationale?: string;
  error?: string | null;
}

export interface ConversationMessage {
  type: 'user' | 'ai';
  content: string;
  timestamp?: number;
}

export interface APIResponse {
  specialistSummary?: string;
  basicPatientSummary?: PatientSummaryItem[];
  populatedTemplate?: TemplateItem[];
  specialistAIResponse?: {
    summaryResponse?: string;
    citations?: any[];
  };
}

interface StreamingContextType {
  streamedData: string;
  parsedData: APIResponse | null;
  clinicalCaseData: ClinicalCaseData | null;
  isStreaming: boolean;
  question: string;
  clinicalNotes: string;
  followUpQuestions: string[];
  isLoadingFollowUp: boolean;
  conversationMessages: ConversationMessage[];
  currentStreamingMessage: string;
  setStreamedData: React.Dispatch<React.SetStateAction<string>>;
  setParsedData: React.Dispatch<React.SetStateAction<APIResponse | null>>;
  setClinicalCaseData: React.Dispatch<React.SetStateAction<ClinicalCaseData | null>>;
  setIsStreaming: (isStreaming: boolean) => void;
  setQuestion: (question: string) => void;
  setClinicalNotes: (notes: string) => void;
  setFollowUpQuestions: React.Dispatch<React.SetStateAction<string[]>>;
  setIsLoadingFollowUp: (isLoading: boolean) => void;
  setConversationMessages: React.Dispatch<React.SetStateAction<ConversationMessage[]>>;
  setCurrentStreamingMessage: React.Dispatch<React.SetStateAction<string>>;
  addUserMessage: (content: string) => void;
  addAIMessage: (content: string) => void;
  resetStream: () => void;
  getAbortController: () => AbortController;
  abortStreaming: () => void;
}

const StreamingContext = createContext<StreamingContextType | undefined>(undefined);

export const StreamingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [streamedData, setStreamedData] = useState('');
  const [parsedData, setParsedData] = useState<APIResponse | null>(null);
  const [clinicalCaseData, setClinicalCaseData] = useState<ClinicalCaseData | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [question, setQuestion] = useState('');
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [followUpQuestions, setFollowUpQuestions] = useState<string[]>([]);
  const [isLoadingFollowUp, setIsLoadingFollowUp] = useState(false);
  const [conversationMessages, setConversationMessages] = useState<ConversationMessage[]>([]);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);

  const getAbortController = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller;
  };

  const abortStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const addUserMessage = (content: string) => {
    setConversationMessages(prev => [...prev, { type: 'user', content, timestamp: Date.now() }]);
  };

  const addAIMessage = (content: string) => {
    setConversationMessages(prev => [...prev, { type: 'ai', content, timestamp: Date.now() }]);
  };

  const resetStream = () => {
    abortStreaming();
    setStreamedData('');
    setParsedData(null);
    setClinicalCaseData(null);
    setIsStreaming(false);
    setQuestion('');
    setClinicalNotes('');
    setFollowUpQuestions([]);
    setIsLoadingFollowUp(false);
    setConversationMessages([]);
    setCurrentStreamingMessage('');
  };

  return (
    <StreamingContext.Provider
      value={{
        streamedData,
        parsedData,
        clinicalCaseData,
        isStreaming,
        question,
        clinicalNotes,
        followUpQuestions,
        isLoadingFollowUp,
        conversationMessages,
        currentStreamingMessage,
        setStreamedData,
        setParsedData,
        setClinicalCaseData,
        setIsStreaming,
        setQuestion,
        setClinicalNotes,
        setFollowUpQuestions,
        setIsLoadingFollowUp,
        setConversationMessages,
        setCurrentStreamingMessage,
        addUserMessage,
        addAIMessage,
        resetStream,
        getAbortController,
        abortStreaming,
      }}
    >
      {children}
    </StreamingContext.Provider>
  );
};

export const useStreaming = () => {
  const context = useContext(StreamingContext);
  if (!context) {
    throw new Error('useStreaming must be used within StreamingProvider');
  }
  return context;
};
